import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Menu, 
  X, 
  ChevronDown,
  User,
  LogOut,
  LayoutDashboard
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Membership', path: '/membership' },
  { name: 'Founding Members', path: '/founding-members' },
  { name: 'Members', path: '/members' },
  { name: 'Events', path: '/events' },
  { name: 'News', path: '/news' },
  { name: 'Documents', path: '/documents' },
  { name: 'Contact', path: '/contact' },
];

// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const Layout = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change and scroll to top
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo-new.png" alt="UIEHEC Logo" className="h-16 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-[#0E285B] ${
                    location.pathname === link.path
                      ? 'text-[#0E285B] border-b-2 border-[#F5A623]'
                      : 'text-slate-600'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <Link to="/verify">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="hidden sm:flex border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10"
                  data-testid="verify-btn"
                >
                  Verify Certificate
                </Button>
              </Link>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                      <User className="h-4 w-4" />
                      <span className="hidden sm:inline">{user.full_name}</span>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to={user.role === 'admin' ? '/admin' : '/member'} className="flex items-center gap-2">
                        <LayoutDashboard className="h-4 w-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" size="sm" data-testid="login-btn">Login</Button>
                  </Link>
                  <Link to="/apply">
                    <Button 
                      size="sm" 
                      className="bg-[#F5A623] text-[#0E285B] hover:bg-[#F5A623]/90 font-semibold"
                      data-testid="apply-btn"
                    >
                      Apply Now
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 mobile-menu-enter">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                      location.pathname === link.path
                        ? 'bg-slate-100 text-[#0E285B]'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    onClick={handleNavClick}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link
                  to="/verify"
                  className="px-4 py-2 text-sm font-medium text-[#F5A623] hover:bg-slate-50 rounded-sm"
                  onClick={handleNavClick}
                >
                  Verify Certificate
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#0E285B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* About */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo-new.png" alt="UIEHEC Logo" className="h-12 w-auto" />
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">
                Union of International Education and Higher Education Centers. 
                Advancing global excellence in higher education.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-['Playfair_Display'] text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {['About', 'Membership', 'Events', 'Contact'].map((item) => (
                  <li key={item}>
                    <Link to={`/${item.toLowerCase()}`} className="text-slate-300 hover:text-[#F5A623] text-sm transition-colors">
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="font-['Playfair_Display'] text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li>
                  <Link 
                    to="/documents" 
                    className="text-slate-300 hover:text-[#F5A623] text-sm transition-colors"
                  >
                    Documents
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/news" 
                    className="text-slate-300 hover:text-[#F5A623] text-sm transition-colors"
                  >
                    News
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/founding-members" 
                    className="text-slate-300 hover:text-[#F5A623] text-sm transition-colors"
                  >
                    Founding Members
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/verify" 
                    className="text-slate-300 hover:text-[#F5A623] text-sm transition-colors"
                  >
                    Verify Certificate
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-['Playfair_Display'] text-lg font-semibold mb-4">Head Office</h4>
              <address className="text-slate-300 text-sm not-italic space-y-2">
                <p>Heinrichsallee 18</p>
                <p>52062 Aachen, Germany</p>
                <p>Email: <a href="mailto:info@uiehec.org" className="hover:text-[#F5A623]">info@uiehec.org</a></p>
                <p>Tel: <a href="tel:+4916059429555" className="hover:text-[#F5A623]">+49 160 594 29 55</a></p>
              </address>
            </div>
          </div>

          <div className="border-t border-slate-700 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-400 text-sm text-center md:text-left">
                © {new Date().getFullYear()} Union of International Education and Higher Education Centers (UIEHEC). All Rights Reserved.
              </p>
            </div>
            <p className="text-slate-500 text-xs text-center mt-4">
              Web Design by{' '}
              <a 
                href="https://yastikmedya.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="relative inline-block text-[#F5A623] font-semibold hover:text-white transition-colors duration-300 group"
              >
                <span className="relative z-10">Yastık Medya SEO Ajansı</span>
                <span className="absolute inset-0 bg-gradient-to-r from-[#F5A623]/0 via-[#F5A623]/30 to-[#F5A623]/0 rounded blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></span>
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
